<template>
  <div class="content">
    <!-- Nagłówek sekcji -->
    <h3 class="center-aligned">{{ t('contacts').toUpperCase() }}:</h3>
    
    <!-- Formularz dodawania pojedynczego kontaktu -->
    <div class="contact-form center-aligned">
      <div class="form-group">
        <label for="contactType" class="label-contact-type">{{ t('contact-type') }}:</label>
        <!-- Wybór typu kontaktu (mail/telefon) -->
        <select id="contactType" v-model.number="contactType">
          <option :value="0">{{ t('email') }}:</option>
          <option :value="1">{{ t('phone-number') }}:</option>
        </select>
      </div>
      <div class="form-group">
        <label for="contactInfo" class="label-contact-info"> {{ t('contact') }}:</label>
        <!-- Pole na mail lub telefon -->
        <input id="contactInfo" v-model="contactInfo" type="text" />
      </div>
      <div class="form-group">
        <label for="description" class="label-description">{{ t('contact-description') }}:</label>
        <!-- Krótki opis -->
        <input id="description" v-model="description" type="text" maxlength="100" />
      </div>
      <!-- Dodaj kontakt, tylko jeśli oba pola wypełnione -->
      <button @click="addContact" :disabled="!contactInfo" class="button-save"> {{ t('add') }}</button>

      <div>
        {{ t('or') }}
      </div>

      <!-- Sekcja dodawania kontaktów z pliku CSV -->
      <div class="file-upload center-aligned">
        <!-- Input do CSV, tylko pliki CSV -->
        <input type="file" @change="handleFileUpload" accept=".csv" ref="fileInput"/>
        <div>
          <!-- Pokazanie nazwy wgranego pliku i przycisk X do usunięcia -->
          <span v-if="uploadedFileName">{{ uploadedFileName }} <button @click="removeFile" class="button-delete">X</button></span>
        </div>
        <!-- Podgląd kontaktów przygotowanych do dodania z CSV -->
        <div v-if="csvContacts.length > 0">
          <h4> {{ t('contacts-from-csv-ready-to-add') }}:</h4>
          <table class="csv-contact-table center-aligned">
            <thead>
              <tr>
                <th>{{ t('ordinal-number') }}</th>
                <th>{{ t('contact-type') }}</th>
                <th>{{ t('contact') }}</th>
                <th>{{ t('description') }}</th>
                <th>{{ t('action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(contact, index) in csvContacts" :key="contact.id">
                <td>{{ index + 1 }}</td>
                <td>{{ contactTypeLabel(contact.type) }}</td>
                <td>{{ contact.info }}</td>
                <td>{{ contact.description }}</td>
                <td><button @click="removeCSVContact(index)" class="button-delete">{{ t('delete') }}</button></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- Przycisk do masowego dodania kontaktów z CSV -->
      <button v-if="csvContacts.length > 0" @click="submitCsvContacts" class="button-save">{{ t('add-contacts-from-csv') }}</button>
      <div class="status-wrap">
        <div
          v-if="statusMessage && !statusDismissed"
          class="alert alert-dismissible fade show mt-2"
          :class="statusType === 'success' ? 'alert-success' : 'alert-danger'"
          role="alert"
        >
          {{ statusMessage }}
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="dismissStatus"
          ></button>
        </div>
      </div>
    </div>

    <!-- Lista zapisanych kontaktów -->
    <div v-if="contacts.length > 0">
      <table class="contact-table center-aligned">
        <thead>
          <tr>
            <th>{{ t('ordinal-number') }}</th>
            <th>{{ t('contact-type') }}</th>
            <th>{{ t('contact') }}</th>
            <th>{{ t('description') }}</th>
            <th>{{ t('action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(contact, index) in paginatedContacts" :key="contact.id">
            <td>{{ index + 1 }}</td>
            <td>{{ contact.type === 0 ? t('email') : t('phone-number') }}</td>
            <td>{{ contact.info }}</td>
            <td>{{ contact.description }}</td>
            <td><button @click="removeContact(contact.id)" class="button-delete">{{ t('delete') }}</button></td>
          </tr>
        </tbody>
      </table>
    </div>
    <!-- Komunikat jeśli brak kontaktów -->
    <div v-else>
      <p class="center-aligned">{{ t('no-contacts-on-list') }}</p>
    </div>

    <h3 class="center-aligned">GRUPY KONTAKTÓW:</h3>
    <div class="group-form center-aligned">
      <div class="form-group">
        <label for="groupName" class="label-group-name">Nazwa grupy:</label>
        <input id="groupName" v-model="groupName" type="text" maxlength="128" />
      </div>
      <div class="form-group">
        <label for="groupDescription" class="label-group-description">Opis grupy:</label>
        <input id="groupDescription" v-model="groupDescription" type="text" maxlength="255" />
      </div>
      <div class="form-group">
        <label for="groupContactSearch" class="label-group-contacts">Kontakty:</label>
        <div class="group-selector">
          <input
            id="groupContactSearch"
            v-model="groupContactSearch"
            type="text"
            placeholder="Szukaj kontaktu..."
          />
          <div class="group-options">
            <label v-for="contact in filteredContactsForGroup" :key="contact.id" class="group-option">
              <input v-model="selectedGroupContactIds" type="checkbox" :value="contact.id" />
              {{ contactTypeLabel(contact.type) }}: {{ contact.info }}
            </label>
            <p v-if="filteredContactsForGroup.length === 0" class="group-empty">Brak kontaktów do wyboru.</p>
          </div>
        </div>
      </div>
      <button @click="addContactGroup" :disabled="!groupName" class="button-save">Utwórz grupę</button>
    </div>

    <div v-if="contactGroups.length > 0">
      <table class="contact-table center-aligned">
        <thead>
          <tr>
            <th>{{ t('ordinal-number') }}</th>
            <th>Nazwa grupy</th>
            <th>Opis</th>
            <th>Kontakty</th>
            <th>{{ t('action') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(group, index) in contactGroups" :key="group.id">
            <td>{{ index + 1 }}</td>
            <td>{{ group.name }}</td>
            <td>{{ group.description }}</td>
            <td>
              <span v-if="group.contactIds.length > 0">
                {{ group.contactIds.map(getContactLabelById).join(', ') }}
              </span>
              <span v-else>-</span>
            </td>
            <td>
              <button @click="startEditGroup(group)" class="button-save">Edytuj</button>
              <button @click="removeContactGroup(group.id)" class="button-delete">{{ t('delete') }}</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <div v-else>
      <p class="center-aligned">Brak grup kontaktów.</p>
    </div>

    <div v-if="editingGroupId" class="group-form center-aligned">
      <h4>Edycja grupy kontaktów</h4>
      <div class="form-group">
        <label for="editGroupName" class="label-group-name">Nazwa grupy:</label>
        <input id="editGroupName" v-model="editGroupName" type="text" maxlength="128" />
      </div>
      <div class="form-group">
        <label for="editGroupDescription" class="label-group-description">Opis grupy:</label>
        <input id="editGroupDescription" v-model="editGroupDescription" type="text" maxlength="255" />
      </div>
      <div class="form-group">
        <label for="editGroupContactSearch" class="label-group-contacts">Kontakty:</label>
        <div class="group-selector">
          <input
            id="editGroupContactSearch"
            v-model="editGroupContactSearch"
            type="text"
            placeholder="Szukaj kontaktu..."
          />
          <div class="group-options">
            <label v-for="contact in filteredContactsForEditGroup" :key="contact.id" class="group-option">
              <input v-model="editSelectedContactIds" type="checkbox" :value="contact.id" />
              {{ contactTypeLabel(contact.type) }}: {{ contact.info }}
            </label>
            <p v-if="filteredContactsForEditGroup.length === 0" class="group-empty">Brak kontaktów do wyboru.</p>
          </div>
        </div>
      </div>
      <button @click="saveEditGroup" :disabled="!editGroupName" class="button-save">Zapisz</button>
      <button @click="cancelEditGroup" class="button-delete">Anuluj</button>
    </div>
  </div>
</template>

<script>
import ContactsListService from '../services/ContactsListService'; // Serwis do API kontaktów
import ContactGroupsService from "@/services/ContactGroupsService";
import Papa from 'papaparse'; // Biblioteka do parsowania CSV

export default {
  name: 'ContactList',
  data() {
    return {
      contactType: 0,           // Wybrany typ kontaktu (0=email, 1=tel)
      contactInfo: '',          // Wpisany email lub telefon
      description: '',          // Opis kontaktu
      contacts: [],             // Lista kontaktów z bazy
      csvContacts: [],          // Lista kontaktów przygotowanych z CSV do dodania
      uploadedFileName: '',     // Nazwa wgranego pliku
      currentPage: 1,           // Numer strony w paginacji
      pageSize: 5,              // Ile kontaktów na stronę
      groupName: '',
      groupDescription: '',
      groupContactSearch: '',
      selectedGroupContactIds: [],
      contactGroups: [],
      editingGroupId: null,
      editGroupName: '',
      editGroupDescription: '',
      editGroupContactSearch: '',
      editSelectedContactIds: [],
      statusMessage: '',
      statusType: 'error',
      statusDismissed: false,
    };
  },
  computed: {
    // Lista kontaktów na bieżącą stronę
    paginatedContacts() {
      const start = (this.currentPage - 1) * this.pageSize;
      const end = this.currentPage * this.pageSize;
      return this.contacts.slice(start, end);
    },
    // Ile stron paginacji
    totalPages() {
      return Math.ceil(this.contacts.length / this.pageSize);
    },
    filteredContactsForGroup() {
      const phrase = this.groupContactSearch.trim().toLowerCase();
      if (!phrase) {
        return this.contacts;
      }

      return this.contacts.filter(contact => (
        String(contact.info ?? '').toLowerCase().includes(phrase)
        || String(contact.description ?? '').toLowerCase().includes(phrase)
      ));
    },
    filteredContactsForEditGroup() {
      const phrase = this.editGroupContactSearch.trim().toLowerCase();
      if (!phrase) {
        return this.contacts;
      }

      return this.contacts.filter(contact => (
        String(contact.info ?? '').toLowerCase().includes(phrase)
        || String(contact.description ?? '').toLowerCase().includes(phrase)
      ));
    }
  },
  methods: {
    normalizeContact(value) {
      return String(value ?? '').trim();
    },
    normalizeDescription(value) {
      return String(value ?? '').trim();
    },
    normalizeContactType(value) {
      if (value === 0 || value === '0' || String(value).toLowerCase() === 'email') {
        return 0;
      }
      if (value === 1 || value === '1' || String(value).toLowerCase() === 'phone') {
        return 1;
      }
      return null;
    },
    contactTypeLabel(type) {
      return Number(type) === 0 ? 'E-mail' : 'Telefon';
    },
    isValidContact(type, value) {
      if (type === 0) {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      }
      if (type === 1) {
        return /^\+48\d{9}$/.test(value);
      }
      return false;
    },
    parseApiError(error, fallbackMessage) {
      if (typeof error === 'string' && error.trim() !== '') {
        return error;
      }
      if (error?.message) {
        return error.message;
      }
      return fallbackMessage;
    },
    isNotFoundError(error) {
      return error === 404 || error === '404' || error?.status === 404 || error?.response?.status === 404;
    },
    mapGroupFromApi(group) {
      return {
        id: group.id,
        name: group.name ?? '',
        description: group.description ?? '',
        contactIds: Array.isArray(group.contactIds) ? group.contactIds : []
      };
    },
    getContactLabelById(contactId) {
      const contact = this.contacts.find(item => item.id === contactId);
      if (!contact) {
        return `#${contactId}`;
      }

      const typeLabel = Number(contact.type) === 0 ? 'E-mail' : 'Telefon';
      return `${typeLabel}: ${contact.info}`;
    },
    // Dodaje kontakt pojedynczo przez formularz
    async addContact() {
      const normalizedType = this.normalizeContactType(this.contactType);
      const normalizedContact = this.normalizeContact(this.contactInfo);
      const normalizedDescription = this.normalizeDescription(this.description);

      if (normalizedType === null) {
        this.showStatus('Nieprawidłowy typ kontaktu.', 'error');
        return;
      }
      if (!normalizedContact) {
        this.showStatus('Pole kontaktu jest wymagane.', 'error');
        return;
      }
      if (!this.isValidContact(normalizedType, normalizedContact)) {
        this.showStatus(
          normalizedType === 0
            ? 'Podaj poprawny adres e-mail.'
            : 'Podaj poprawny numer telefonu w formacie +48XXXXXXXXX.',
          'error'
        );
        return;
      }

      try {
        const response = await ContactsListService.postNewContact(
          normalizedContact,
          normalizedType,
          normalizedDescription
        );
        // Dodanie do lokalnej listy (po udanej odpowiedzi z serwera)
        this.contacts.unshift({
          type: response.typeOfContact,
          info: response.contact,
          description: response.description ?? '',
          id: response.id,
        });
        // Reset pól formularza
        this.contactType = 0;
        this.contactInfo = '';
        this.description = '';
        await this.fetchContactGroups();
        this.showStatus('Kontakt został dodany.', 'success');
      } catch (error) {
        console.error('Error adding contact:', error);
        this.showStatus(this.parseApiError(error, 'Nie udało się dodać kontaktu.'), 'error');
      }
    },
    // Pobiera kontakty z serwera (wywoływane przy montowaniu)
    async fetchContacts() {
      try {
        const response = await ContactsListService.getAll();
        // Mapowanie pól API na lokalne obiekty
        this.contacts = (Array.isArray(response) ? response : []).map(contact => ({
          type: contact.typeOfContact,
          info: contact.contact,
          description: contact.description,
          id: contact.id,
        }));
      } catch (error) {
        if (this.isNotFoundError(error)) {
          this.contacts = [];
          this.statusMessage = '';
          this.statusDismissed = false;
          return;
        }
        console.error('Error fetching contacts:', error);
        this.contacts = [];
      }
    },
    async fetchContactGroups() {
      try {
        const response = await ContactGroupsService.getAll();
        this.contactGroups = (Array.isArray(response) ? response : []).map(this.mapGroupFromApi);
      } catch (error) {
        if (this.isNotFoundError(error)) {
          this.contactGroups = [];
          this.statusMessage = '';
          this.statusDismissed = false;
          return;
        }
        console.error('Error fetching contact groups:', error);
        this.contactGroups = [];
      }
    },
    async addContactGroup() {
      const normalizedName = String(this.groupName ?? '').trim();
      const normalizedDescription = this.normalizeDescription(this.groupDescription);
      const contactIds = [...new Set(this.selectedGroupContactIds.map(id => Number(id)).filter(Number.isInteger))];

      if (!normalizedName) {
        this.showStatus('Nazwa grupy jest wymagana.', 'error');
        return;
      }

      try {
        const response = await ContactGroupsService.add(normalizedName, normalizedDescription, contactIds);
        this.contactGroups.unshift(this.mapGroupFromApi(response));
        this.groupName = '';
        this.groupDescription = '';
        this.groupContactSearch = '';
        this.selectedGroupContactIds = [];
        this.showStatus('Grupa kontaktów została utworzona.', 'success');
      } catch (error) {
        console.error('Error adding contact group:', error);
        this.showStatus(this.parseApiError(error, 'Nie udało się utworzyć grupy kontaktów.'), 'error');
      }
    },
    startEditGroup(group) {
      this.editingGroupId = group.id;
      this.editGroupName = group.name ?? '';
      this.editGroupDescription = group.description ?? '';
      this.editGroupContactSearch = '';
      this.editSelectedContactIds = [...(group.contactIds ?? [])];
    },
    cancelEditGroup() {
      this.editingGroupId = null;
      this.editGroupName = '';
      this.editGroupDescription = '';
      this.editGroupContactSearch = '';
      this.editSelectedContactIds = [];
    },
    async saveEditGroup() {
      const normalizedName = String(this.editGroupName ?? '').trim();
      const normalizedDescription = this.normalizeDescription(this.editGroupDescription);
      const contactIds = [...new Set(this.editSelectedContactIds.map(id => Number(id)).filter(Number.isInteger))];

      if (!this.editingGroupId) {
        return;
      }
      if (!normalizedName) {
        this.showStatus('Nazwa grupy jest wymagana.', 'error');
        return;
      }

      try {
        const response = await ContactGroupsService.update(
          this.editingGroupId,
          normalizedName,
          normalizedDescription,
          contactIds
        );

        this.contactGroups = this.contactGroups.map(group =>
          group.id === this.editingGroupId ? this.mapGroupFromApi(response) : group
        );

        this.cancelEditGroup();
        this.showStatus('Grupa kontaktów została zaktualizowana.', 'success');
      } catch (error) {
        console.error('Error updating contact group:', error);
        this.showStatus(this.parseApiError(error, 'Nie udało się zaktualizować grupy kontaktów.'), 'error');
      }
    },
    async removeContactGroup(id) {
      try {
        await ContactGroupsService.delete(id);
        this.contactGroups = this.contactGroups.filter(group => group.id !== id);
        if (this.editingGroupId === id) {
          this.cancelEditGroup();
        }
        this.showStatus('Grupa kontaktów została usunięta.', 'success');
      } catch (error) {
        console.error('Error deleting contact group:', error);
        this.showStatus(this.parseApiError(error, 'Nie udało się usunąć grupy kontaktów.'), 'error');
      }
    },
    // Usuwa kontakt po id
    async removeContact(id) {
      try {
        await ContactsListService.delete(id);
        // Usunięcie z lokalnej listy
        this.contacts = this.contacts.filter(contact => contact.id !== id);
        this.selectedGroupContactIds = this.selectedGroupContactIds.filter(contactId => contactId !== id);
        this.editSelectedContactIds = this.editSelectedContactIds.filter(contactId => contactId !== id);
        await this.fetchContactGroups();
        this.showStatus('Kontakt został usunięty.', 'success');
      } catch (error) {
        console.error('Error deleting contact:', error);
        this.showStatus('Nie udało się usunąć kontaktu.', 'error');
      }
    },
    // Obsługa paginacji: następna strona
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },
    // Obsługa paginacji: poprzednia strona
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },
    // Obsługa uploadu CSV
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        this.uploadedFileName = file.name;
        // Parsowanie CSV - oddzielone średnikami
        Papa.parse(file, {
          header: false,
          skipEmptyLines: true,
          delimiter: ';',
          complete: this.addContactsFromCSV,
          error: (error) => {
            console.error('Error reading CSV file:', error);
            this.showStatus('Nie udało się odczytać pliku CSV.', 'error');
          }
        });
      }
    },
    // Callback po sparsowaniu CSV - uzupełnia tablicę csvContacts
    addContactsFromCSV(results) {
      this.csvContacts = results.data
        .map(contact => ({
        type: this.normalizeContactType(contact[0]),
        info: this.normalizeContact(contact[1]),
        description: this.normalizeDescription(contact[2]),
        // monitoredSince: new Date().toISOString(), // Przykład - nieużywane
        id: Date.now() + Math.random() // Unikalne tymczasowe id
      }))
      .filter(contact => (
        contact.type !== null
        && contact.info
        && this.isValidContact(contact.type, contact.info)
      ));
    },
    // Dodaje wszystkie kontakty z CSV do bazy
    async submitCsvContacts() {
      let failed = false;
      for (let contact of this.csvContacts) {
        try {
          if (contact.type === null || !contact.info || !this.isValidContact(contact.type, contact.info)) {
            failed = true;
            continue;
          }
          const response = await ContactsListService.postNewContact(
            contact.info,
            contact.type,
            contact.description
          );
          this.contacts.unshift({
            type: response.typeOfContact,
            info: response.contact,
            description: response.description ?? '',
            id: response.id
          });
        } catch (error) {
          console.error('Error adding contact from CSV:', error);
          failed = true;
        }
      }
      // Czyszczenie stanu CSV po udanym dodaniu
      this.csvContacts = [];
      this.uploadedFileName = '';
      this.$refs.fileInput.value = '';
      this.showStatus(
        failed
          ? 'Część kontaktów z CSV nie została dodana.'
          : 'Kontakty z pliku CSV zostały dodane.',
        failed ? 'error' : 'success'
      );
    },
    // Usuwa nazwę pliku i listę csvContacts
    removeFile() {
      this.uploadedFileName = '';
      this.csvContacts = [];
      this.$refs.fileInput.value = '';
    },
    // Usuwa pojedynczy kontakt z csvContacts (jeszcze nie dodany do bazy)
    removeCSVContact(index) {
      this.csvContacts.splice(index, 1);
    },
    showStatus(message, type = 'error') {
      this.statusMessage = message;
      this.statusType = type;
      this.statusDismissed = false;
    },
    dismissStatus() {
      this.statusDismissed = true;
    },
  },
  // Automatycznie pobiera kontakty po załadowaniu komponentu
  async mounted() {
    await this.fetchContacts();
    await this.fetchContactGroups();
  }
};
</script>

<style scoped>
h3 {
  padding: 1rem;
  font-weight: bold;
  border-bottom: 1px solid #aeaeae;
}

.contact-form {
  margin-bottom: 20px;
  margin-top: 1.5rem;
  border-bottom: 1px solid #aeaeae;
}

.group-form {
  margin-bottom: 20px;
  margin-top: 1.5rem;
  border-bottom: 1px solid #aeaeae;
}

.contact-form .form-group {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-right: 10rem;
}

.group-form .form-group {
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-right: 10rem;
}

select {
  width: 192px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
  margin-bottom: 1rem;
}

.contact-form .form-group .label-contact-type {
  padding-right: 3.65rem;
}

.contact-form .form-group .label-contact-info {
  padding-right: 6rem;
}

.contact-form .form-group .label-description {
  padding-right: 3.25rem;
}

.group-form .form-group .label-group-name {
  padding-right: 3.1rem;
}

.group-form .form-group .label-group-description {
  padding-right: 4rem;
}

.group-form .form-group .label-group-contacts {
  padding-right: 6.1rem;
}

.contact-form button:not(.btn-close) {
  margin-top: 1rem;
  width: 194px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
  margin-bottom: 1.5rem;
}

.group-form button:not(.btn-close) {
  margin-top: 1rem;
  width: 194px;
  padding: 0.5rem;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
  margin-bottom: 1.5rem;
}

.contact-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.contact-table th,
.contact-table td {
  border: 1px solid #ccc;
  padding: 10px;
}

.contact-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
}

.pagination button {
  padding: 5px 10px;
  margin: 0 5px;
}
  
p {
  font-size: large;
}

.file-upload {
  margin: 20px 0;
}

.file-upload span {
  margin-left: 10px;
}

.file-upload button {
  background: none;
  border: none;
  color: red;
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
}

.csv-contact-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
}

.csv-contact-table th,
.csv-contact-table td {
  border: 1px solid #ccc;
  padding: 10px;
}

.csv-contact-table td button {
  width: 75%;
  border: 1px solid #ccc;
  background-color: #f0f0f0;
  color: black;
  box-shadow: 2px 2px 5px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  transition: background-color 0.3s, color 0.3s, transform 0.3s;
}

.button-delete {
  padding: 0.25rem 1rem 0.25rem 1rem;
}

.button-save {
  margin-right: 3px;
}

.group-selector {
  width: 420px;
}

.group-selector input[type="text"] {
  width: 100%;
  padding: 0.45rem;
  border: 1px solid #ccc;
}

.group-options {
  max-height: 160px;
  overflow-y: auto;
  border: 1px solid #ccc;
  border-top: none;
  text-align: left;
  background: #fff;
}

.group-option {
  display: block;
  padding: 0.3rem 0.5rem;
}

.group-empty {
  margin: 0;
  padding: 0.5rem;
  font-size: 0.9rem;
}

.alert-success {
  color: #155724;
  background-color: #d4edda;
  border-color: #c3e6cb;
}

.status-wrap {
  width: 55%;
  margin: 0.5rem auto 0;
}

.status-wrap .alert {
  margin-bottom: 0.5rem;
}

.status-wrap .btn-close {
  box-sizing: content-box;
  width: 1em;
  height: 1em;
  padding: 0.25em;
  color: #000;
  background: transparent
    url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e")
    center/1em auto no-repeat;
  border: 0;
  border-radius: 0.375rem;
  opacity: 0.5;
}

.status-wrap .alert-dismissible .btn-close {
  top: 50%;
  right: 0.75rem;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.status-wrap .btn-close:hover {
  opacity: 0.75;
}

.button-save:hover {
  background-color: #006f17 !important;
  border: 1px solid #006f17 !important;
}

.button-delete {
  padding: 0.45rem 1rem 0.5rem 1rem;
}

.button-delete:hover {
  background-color: #e14544 !important;
  color: white !important;
}
</style>
